<template>
  <div class="market-page">
    <div class="page-top-bar">
      <div class="page-top-left">
        <h2 class="page-title">市场行情</h2>
        <span class="update-time">
          <i class="el-icon-time"></i>
          最后更新：{{ lastUpdated }}
        </span>
      </div>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">市场类型</span>
        <el-select v-model="marketType" size="small" style="width: 160px">
          <el-option
            v-for="item in marketTypes"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">时间范围</span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="small"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
        />
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row">
      <stat-card
        v-for="card in summaryCards"
        :key="card.key"
        v-bind="card"
      />
    </div>

    <div class="chart-section">
      <chart-card title="价格走势" class="price-chart">
        <base-chart :option="priceChartOption" />
      </chart-card>
      <chart-card title="各市场占比" class="share-chart">
        <base-chart :option="shareChartOption" />
      </chart-card>
    </div>

    <div class="table-section">
      <el-table :data="marketList" border stripe size="small">
        <el-table-column prop="date" label="日期" width="120" sortable />
        <el-table-column prop="marketName" label="市场名称" width="140" />
        <el-table-column prop="open" label="开盘价 (元/tCO2)" min-width="140" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.open, 2) }}</template>
        </el-table-column>
        <el-table-column prop="close" label="收盘价 (元/tCO2)" min-width="140" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.close, 2) }}</template>
        </el-table-column>
        <el-table-column prop="high" label="最高价 (元/tCO2)" min-width="140" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.high, 2) }}</template>
        </el-table-column>
        <el-table-column prop="low" label="最低价 (元/tCO2)" min-width="140" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.low, 2) }}</template>
        </el-table-column>
        <el-table-column prop="volume" label="成交量 (万吨)" min-width="130" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.volume, 2) }}</template>
        </el-table-column>
        <el-table-column prop="changeRate" label="涨跌幅 (%)" width="120" align="right">
          <template slot-scope="{ row }">
            <span :class="row.changeRate >= 0 ? 'text-danger' : 'text-success'">
              {{ row.changeRate >= 0 ? '+' : '' }}{{ formatNumber(row.changeRate, 2) }}%
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getMarketData } from '@/api/modules/carbonAsset'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonAssetMarket',
  components: { FilterBar, StatCard, ChartCard, BaseChart },
  data() {
    return {
      lastUpdated: '',
      marketTypes: [],
      marketType: '全国碳市场',
      dateRange: ['2025-06-01', '2025-07-07'],
      summary: {
        latestPrice: { today: 0, annual: 0, unit: '元/tCO2' },
        dailyChange: { today: 0, annual: 0, unit: '%' },
        volume: { today: 0, annual: 0, unit: '万吨' },
        turnover: { today: 0, annual: 0, unit: '万元' }
      },
      marketList: [],
      priceChartOption: {},
      shareChartOption: {}
    }
  },
  computed: {
    summaryCards() {
      const { latestPrice, dailyChange, volume, turnover } = this.summary
      const changeColor = dailyChange.today >= 0 ? '#F56C6C' : '#67C23A'
      return [
        {
          key: 'latestPrice',
          title: '最新收盘价',
          today: latestPrice.today,
          annual: latestPrice.annual,
          unit: latestPrice.unit,
          annualUnit: latestPrice.unit,
          icon: 'el-icon-coin',
          color: '#409EFF'
        },
        {
          key: 'dailyChange',
          title: '日涨跌幅',
          today: dailyChange.today,
          annual: dailyChange.annual,
          unit: dailyChange.unit,
          annualUnit: dailyChange.unit,
          icon: 'el-icon-top',
          color: changeColor
        },
        {
          key: 'volume',
          title: '成交量',
          today: volume.today,
          annual: volume.annual,
          unit: volume.unit,
          annualUnit: volume.unit,
          icon: 'el-icon-s-data',
          color: '#E6A23C'
        },
        {
          key: 'turnover',
          title: '成交额',
          today: turnover.today,
          annual: turnover.annual,
          unit: turnover.unit,
          annualUnit: turnover.unit,
          icon: 'el-icon-money',
          color: '#909399'
        }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getMarketData({
        marketType: this.marketType,
        startDate: this.dateRange && this.dateRange[0],
        endDate: this.dateRange && this.dateRange[1]
      })
      const data = res.data
      this.lastUpdated = data.lastUpdated
      this.marketTypes = data.marketTypes
      this.summary = data.summary
      this.marketList = data.marketList
      this.buildPriceChart(data.priceTrend)
      this.buildShareChart(data.marketShare)
    },
    buildPriceChart(trend) {
      this.priceChartOption = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['收盘价', '成交量'], bottom: 0 },
        grid: { left: 60, right: 60, top: 30, bottom: 50 },
        xAxis: { type: 'category', data: trend.dates },
        yAxis: [
          { type: 'value', name: '元/tCO2', position: 'left' },
          { type: 'value', name: '万吨', position: 'right' }
        ],
        series: [
          {
            name: '收盘价',
            type: 'line',
            yAxisIndex: 0,
            data: trend.closePrice,
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#409EFF', width: 2 },
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '成交量',
            type: 'bar',
            yAxisIndex: 1,
            data: trend.volume,
            itemStyle: { color: 'rgba(26, 58, 92, 0.6)' }
          }
        ]
      }
    },
    buildShareChart(share) {
      this.shareChartOption = {
        tooltip: { trigger: 'item', formatter: '{b}: {c}% ({d}%)' },
        legend: { orient: 'vertical', right: 10, top: 'center' },
        color: ['#409EFF', '#1a3a5c', '#E6A23C', '#67C23A'],
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['40%', '50%'],
          data: share,
          label: { formatter: '{b}\n{d}%' }
        }]
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .page-top-left {
    display: flex;
    align-items: baseline;
    gap: 16px;
  }

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }

  .update-time {
    font-size: 13px;
    color: @text-secondary;

    i {
      margin-right: 4px;
    }
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-section {
  display: flex;
  gap: 16px;
  height: 360px;
  margin-bottom: 16px;

  .price-chart,
  .share-chart {
    flex: 1;
  }
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  overflow-x: auto;
}
</style>
